/**
 * ConversionScrubPage — FS-823 Wave 3 dash.
 *
 * Operator surface for reviewing pending conversions. Key flows:
 *   1. List /api/v1/conversion-scrub?status=pending with date / campaign /
 *      affiliate / fraud-score filters.
 *   2. Select rows via a leading checkbox column (single + select-all).
 *   3. Bulk approve or reject with a required reason code via one PATCH call.
 *
 * Row-level dropdown also exposes single-row Approve / Reject for fast triage.
 *
 * Design contract: emerald/sky brand headline, zinc borders, rounded-[6-8px],
 * amber/red accents reserved for fraud bands. No emojis, no competitor names.
 * Raw fetch() lives in ./api only (rule D6).
 */
import {
	type ChangeEvent,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from "react";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
	type ColumnDef,
	type Row,
} from "@tanstack/react-table";
import {
	REASON_CODES,
	REASON_LABELS,
	formatCurrency,
	formatDateTime,
	fraudBand,
	isReasonCode,
	maskIp,
	type BulkDecision,
	type ConversionScrubRow,
	type ReasonCode,
} from "./api";
import { useBulkDecide, useConversionScrubList } from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — keep aligned to DESIGN.md (light theme only).
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-xs font-medium text-zinc-500 uppercase tracking-wider";
const INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT =
	"rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-600 bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 hover:border-red-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Filter state — all optional, server applies.
// ---------------------------------------------------------------------------

interface FilterState {
	campaign: string;
	affiliate: string;
	fraudMin: string;
	from: string;
	to: string;
}

const EMPTY_FILTERS: FilterState = {
	campaign: "",
	affiliate: "",
	fraudMin: "",
	from: "",
	to: "",
};

function toQueryArgs(f: FilterState) {
	const q: {
		campaign?: string;
		affiliate?: string;
		fraud_min?: number;
		from?: string;
		to?: string;
	} = {};
	if (f.campaign.trim()) q.campaign = f.campaign.trim();
	if (f.affiliate.trim()) q.affiliate = f.affiliate.trim();
	const n = Number.parseInt(f.fraudMin, 10);
	if (Number.isFinite(n) && n >= 0 && n <= 100) q.fraud_min = n;
	if (f.from) q.from = f.from;
	if (f.to) q.to = f.to;
	return q;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ConversionScrubPage() {
	const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
	const [reason, setReason] = useState<ReasonCode>("fraud");
	const [note, setNote] = useState("");
	const [selected, setSelected] = useState<Record<string, boolean>>({});

	const queryArgs = useMemo(() => toQueryArgs(filters), [filters]);
	const listQuery = useConversionScrubList(queryArgs);
	const bulkMutation = useBulkDecide();

	const rows = listQuery.data?.items ?? [];

	const selectedIds = useMemo(
		() => Object.keys(selected).filter((k) => selected[k]),
		[selected],
	);
	const selectedCount = selectedIds.length;

	const clearSelection = useCallback(() => setSelected({}), []);

	const handleBulk = useCallback(
		(decision: BulkDecision) => {
			if (selectedCount === 0) return;
			bulkMutation.mutate(
				{
					ids: selectedIds,
					decision,
					reason,
					note: note.trim() || undefined,
				},
				{
					onSuccess: () => {
						clearSelection();
						setNote("");
					},
				},
			);
		},
		[bulkMutation, clearSelection, note, reason, selectedCount, selectedIds],
	);

	const handleRowDecision = useCallback(
		(id: string, decision: BulkDecision) => {
			bulkMutation.mutate({
				ids: [id],
				decision,
				reason,
				note: note.trim() || undefined,
			});
		},
		[bulkMutation, note, reason],
	);

	return (
		<main
			data-testid="conversion-scrub-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8"
		>
			<header className="flex flex-col gap-2">
				<p className={LABEL}>Conversions</p>
				<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-2xl font-semibold text-transparent">
					Conversion scrub
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Review pending conversions flagged for manual scrub. Select rows and
					bulk-approve clean traffic, or bulk-reject with a reason code
					(fraud, test, duplicate, unqualified, other).
				</p>
			</header>

			<FiltersCard
				filters={filters}
				onChange={setFilters}
				onClear={() => setFilters(EMPTY_FILTERS)}
				disabled={bulkMutation.isPending || listQuery.isLoading}
				resultCount={rows.length}
				loading={listQuery.isLoading}
			/>

			<StickyToolbar
				selectedCount={selectedCount}
				reason={reason}
				onReasonChange={setReason}
				note={note}
				onNoteChange={setNote}
				busy={bulkMutation.isPending}
				onApprove={() => handleBulk("approved")}
				onReject={() => handleBulk("rejected")}
				onClear={clearSelection}
			/>

			<section
				className={`${CARD} flex flex-col`}
				aria-label="Pending conversions"
			>
				<header className="flex items-center justify-between border-b border-zinc-200 p-4">
					<div>
						<h2 className="text-base font-medium text-zinc-900">
							Pending conversions
						</h2>
						<p className="text-xs text-zinc-500">
							{listQuery.isLoading
								? "Loading pending conversions."
								: rows.length === 0
									? "No pending conversions for the current filters."
									: `${rows.length} conversion${rows.length === 1 ? "" : "s"} awaiting review.`}
						</p>
					</div>
				</header>

				{listQuery.isLoading ? (
					<div
						className="p-10 text-center text-sm text-zinc-500"
						data-testid="conversion-scrub-loading"
					>
						Loading pending conversions.
					</div>
				) : listQuery.error ? (
					<div
						className="p-5 text-sm text-red-600"
						role="alert"
						data-testid="conversion-scrub-error"
					>
						{listQuery.error.message}
					</div>
				) : rows.length === 0 ? (
					<EmptyState />
				) : (
					<ScrubTable
						rows={rows}
						selected={selected}
						onSelectedChange={setSelected}
						busy={bulkMutation.isPending}
						onRowApprove={(id) => handleRowDecision(id, "approved")}
						onRowReject={(id) => handleRowDecision(id, "rejected")}
					/>
				)}
			</section>
		</main>
	);
}

export default ConversionScrubPage;

// ---------------------------------------------------------------------------
// Filters card
// ---------------------------------------------------------------------------

function FiltersCard(props: {
	filters: FilterState;
	onChange: (next: FilterState) => void;
	onClear: () => void;
	disabled: boolean;
	resultCount: number;
	loading: boolean;
}) {
	const set = (patch: Partial<FilterState>) =>
		props.onChange({ ...props.filters, ...patch });
	return (
		<section
			className={`${CARD} p-4`}
			aria-label="Filters"
			data-testid="filters-card"
		>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
				<LabeledInput
					id="filter-from"
					label="From"
					type="date"
					value={props.filters.from}
					onChange={(v) => set({ from: v })}
					disabled={props.disabled}
				/>
				<LabeledInput
					id="filter-to"
					label="To"
					type="date"
					value={props.filters.to}
					onChange={(v) => set({ to: v })}
					disabled={props.disabled}
				/>
				<LabeledInput
					id="filter-campaign"
					label="Campaign"
					type="text"
					placeholder="Campaign id"
					value={props.filters.campaign}
					onChange={(v) => set({ campaign: v })}
					disabled={props.disabled}
				/>
				<LabeledInput
					id="filter-affiliate"
					label="Affiliate"
					type="text"
					placeholder="Affiliate id"
					value={props.filters.affiliate}
					onChange={(v) => set({ affiliate: v })}
					disabled={props.disabled}
				/>
				<LabeledInput
					id="filter-fraud-min"
					label="Min fraud score"
					type="number"
					placeholder="0-100"
					value={props.filters.fraudMin}
					onChange={(v) => set({ fraudMin: v })}
					disabled={props.disabled}
				/>
			</div>
			<div className="mt-3 flex items-center justify-between">
				<p className="text-xs text-zinc-500">
					{props.loading
						? "Applying filters."
						: `${props.resultCount} result${props.resultCount === 1 ? "" : "s"}.`}
				</p>
				<button
					type="button"
					onClick={props.onClear}
					className={BTN_GHOST}
					disabled={props.disabled}
					data-testid="filters-clear"
				>
					Clear filters
				</button>
			</div>
		</section>
	);
}

function LabeledInput(props: {
	id: string;
	label: string;
	type: string;
	value: string;
	onChange: (next: string) => void;
	placeholder?: string;
	disabled?: boolean;
}) {
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			<input
				id={props.id}
				type={props.type}
				value={props.value}
				placeholder={props.placeholder}
				disabled={props.disabled}
				className={INPUT}
				onChange={(e: ChangeEvent<HTMLInputElement>) =>
					props.onChange(e.currentTarget.value)
				}
				data-testid={`input-${props.id}`}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sticky bulk-action toolbar — appears when >=1 selected.
// ---------------------------------------------------------------------------

function StickyToolbar(props: {
	selectedCount: number;
	reason: ReasonCode;
	onReasonChange: (next: ReasonCode) => void;
	note: string;
	onNoteChange: (next: string) => void;
	busy: boolean;
	onApprove: () => void;
	onReject: () => void;
	onClear: () => void;
}) {
	const active = props.selectedCount > 0;
	return (
		<div
			data-testid="bulk-toolbar"
			className={[
				"sticky top-2 z-20 transition-opacity",
				active ? "opacity-100" : "pointer-events-none opacity-0",
			].join(" ")}
			aria-hidden={!active}
		>
			<div
				className={`${CARD} flex flex-col gap-3 border-emerald-200 bg-emerald-50/60 p-3 sm:flex-row sm:items-center sm:justify-between`}
			>
				<div className="flex items-center gap-3 text-sm text-zinc-800">
					<span
						className="inline-flex items-center rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-xs font-medium text-emerald-700"
						data-testid="bulk-selected-count"
					>
						{props.selectedCount} selected
					</span>
					<button
						type="button"
						onClick={props.onClear}
						className="text-xs font-medium text-zinc-500 underline decoration-dotted underline-offset-4 hover:text-zinc-700"
						data-testid="bulk-clear"
					>
						Clear selection
					</button>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<label htmlFor="bulk-reason" className={LABEL}>
						Reason
					</label>
					<select
						id="bulk-reason"
						value={props.reason}
						onChange={(e) => {
							const v = e.currentTarget.value;
							if (isReasonCode(v)) props.onReasonChange(v);
						}}
						className={SELECT}
						data-testid="bulk-reason"
					>
						{REASON_CODES.map((r) => (
							<option key={r} value={r}>
								{REASON_LABELS[r]}
							</option>
						))}
					</select>
					<input
						id="bulk-note"
						type="text"
						placeholder="Optional note"
						value={props.note}
						onChange={(e) => props.onNoteChange(e.currentTarget.value)}
						className={`${INPUT} sm:w-56`}
						maxLength={500}
						data-testid="bulk-note"
					/>
					<button
						type="button"
						onClick={props.onApprove}
						disabled={props.busy || !active}
						className={BTN_PRIMARY}
						data-testid="bulk-approve"
					>
						{props.busy ? "Working…" : `Approve Selected (${props.selectedCount})`}
					</button>
					<button
						type="button"
						onClick={props.onReject}
						disabled={props.busy || !active}
						className={BTN_DANGER}
						data-testid="bulk-reject"
					>
						{props.busy ? "Working…" : `Reject Selected (${props.selectedCount})`}
					</button>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Data table via @tanstack/react-table
// ---------------------------------------------------------------------------

function ScrubTable(props: {
	rows: readonly ConversionScrubRow[];
	selected: Record<string, boolean>;
	onSelectedChange: (next: Record<string, boolean>) => void;
	busy: boolean;
	onRowApprove: (id: string) => void;
	onRowReject: (id: string) => void;
}) {
	const { rows, selected, onSelectedChange, busy, onRowApprove, onRowReject } =
		props;

	const toggleRow = useCallback(
		(id: string) => {
			onSelectedChange({ ...selected, [id]: !selected[id] });
		},
		[onSelectedChange, selected],
	);

	const toggleAll = useCallback(
		(checked: boolean) => {
			if (!checked) {
				onSelectedChange({});
				return;
			}
			const next: Record<string, boolean> = {};
			for (const r of rows) next[r.id] = true;
			onSelectedChange(next);
		},
		[onSelectedChange, rows],
	);

	const allChecked = rows.length > 0 && rows.every((r) => selected[r.id]);
	const someChecked = rows.some((r) => selected[r.id]) && !allChecked;

	const columns = useMemo<ColumnDef<ConversionScrubRow>[]>(
		() => [
			{
				id: "select",
				header: () => (
					<input
						type="checkbox"
						className="h-4 w-4 cursor-pointer accent-emerald-500"
						checked={allChecked}
						ref={(el) => {
							if (el) el.indeterminate = someChecked;
						}}
						onChange={(e) => toggleAll(e.currentTarget.checked)}
						aria-label="Select all"
						data-testid="select-all"
					/>
				),
				cell: ({ row }) => (
					<input
						type="checkbox"
						className="h-4 w-4 cursor-pointer accent-emerald-500"
						checked={Boolean(selected[row.original.id])}
						onChange={() => toggleRow(row.original.id)}
						aria-label={`Select ${row.original.id}`}
						data-testid={`select-${row.original.id}`}
					/>
				),
			},
			{
				id: "timestamp",
				header: "Timestamp",
				cell: ({ row }) => (
					<span className="text-xs text-zinc-600">
						{formatDateTime(row.original.timestamp || row.original.created_at)}
					</span>
				),
			},
			{
				id: "affiliate",
				header: "Affiliate",
				cell: ({ row }) => (
					<span className="text-sm text-zinc-800">
						{row.original.affiliate_name || row.original.affiliate_id || "—"}
					</span>
				),
			},
			{
				id: "campaign",
				header: "Campaign",
				cell: ({ row }) => (
					<span className="text-sm text-zinc-800">
						{row.original.campaign_name || row.original.campaign_id || "—"}
					</span>
				),
			},
			{
				id: "offer",
				header: "Offer",
				cell: ({ row }) => (
					<span className="text-sm text-zinc-700">
						{row.original.offer_name || row.original.offer_id || "—"}
					</span>
				),
			},
			{
				id: "payout",
				header: "Payout",
				cell: ({ row }) => (
					<span className="tabular-nums text-sm text-zinc-900">
						{formatCurrency(row.original.payout_cents)}
					</span>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: () => <StatusPill status="pending" />,
			},
			{
				id: "ip",
				header: "IP",
				cell: ({ row }) => {
					const ip = row.original.ip_address ?? null;
					return (
						<span
							className="font-mono text-xs text-zinc-600"
							title={ip ?? undefined}
						>
							{maskIp(ip)}
						</span>
					);
				},
			},
			{
				id: "fraud",
				header: "Fraud",
				cell: ({ row }) => <FraudBadge score={row.original.fraud_score} />,
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<RowActions
						id={row.original.id}
						busy={busy}
						onApprove={() => onRowApprove(row.original.id)}
						onReject={() => onRowReject(row.original.id)}
					/>
				),
			},
		],
		[
			allChecked,
			busy,
			onRowApprove,
			onRowReject,
			selected,
			someChecked,
			toggleAll,
			toggleRow,
		],
	);

	const table = useReactTable({
		data: rows as ConversionScrubRow[],
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="conversion-scrub-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wider text-zinc-500">
					{table.getHeaderGroups().map((hg) => (
						<tr key={hg.id}>
							{hg.headers.map((h) => (
								<th
									key={h.id}
									scope="col"
									className="px-4 py-3 text-left font-medium"
								>
									{h.isPlaceholder
										? null
										: flexRender(h.column.columnDef.header, h.getContext())}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{table.getRowModel().rows.map((row: Row<ConversionScrubRow>) => {
						const isSelected = Boolean(selected[row.original.id]);
						return (
							<tr
								key={row.id}
								data-testid={`row-${row.original.id}`}
								data-selected={isSelected ? "true" : "false"}
								className={[
									"transition-colors",
									isSelected
										? "bg-emerald-50/40"
										: "hover:bg-emerald-50/30",
								].join(" ")}
							>
								{row.getVisibleCells().map((cell) => (
									<td key={cell.id} className="px-4 py-3 align-middle">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Row actions dropdown — lightweight (no portal).
// ---------------------------------------------------------------------------

function RowActions(props: {
	id: string;
	busy: boolean;
	onApprove: () => void;
	onReject: () => void;
}) {
	const [open, setOpen] = useState(false);
	return (
		<div className="relative inline-block text-left">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				disabled={props.busy}
				className={BTN_NEUTRAL}
				data-testid={`row-actions-${props.id}`}
				aria-haspopup="menu"
				aria-expanded={open}
			>
				Actions
			</button>
			{open ? (
				<div
					role="menu"
					className="absolute right-0 z-10 mt-1 w-40 rounded-[6px] border border-zinc-200 bg-white py-1 shadow-lg"
					data-testid={`row-menu-${props.id}`}
				>
					<button
						type="button"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							props.onApprove();
						}}
						className="block w-full px-3 py-1.5 text-left text-sm text-emerald-700 hover:bg-emerald-50"
					>
						Approve
					</button>
					<button
						type="button"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							props.onReject();
						}}
						className="block w-full px-3 py-1.5 text-left text-sm text-red-700 hover:bg-red-50"
					>
						Reject
					</button>
				</div>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------

function StatusPill(props: { status: "pending" }) {
	return (
		<span
			className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
			data-testid={`status-${props.status}`}
		>
			Pending
		</span>
	);
}

function FraudBadge(props: { score: number | null | undefined }) {
	const score =
		typeof props.score === "number" && Number.isFinite(props.score)
			? props.score
			: 0;
	const band = fraudBand(score);
	const tone =
		band === "high"
			? "border-red-200 bg-red-50 text-red-700"
			: band === "medium"
				? "border-amber-200 bg-amber-50 text-amber-700"
				: "border-emerald-200 bg-emerald-50 text-emerald-700";
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums ${tone}`}
			data-testid={`fraud-${band}`}
		>
			{score}
		</span>
	);
}

function EmptyState(): ReactNode {
	return (
		<div
			className="flex flex-col items-center gap-2 p-12 text-center"
			data-testid="conversion-scrub-empty"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
				<svg
					aria-hidden="true"
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="m5 10 3 3 7-7" />
				</svg>
			</div>
			<p className="text-base font-semibold text-zinc-900">Queue is clear</p>
			<p className="max-w-sm text-sm text-zinc-500">
				No pending conversions match the current filters. Adjust filters or
				check back when new scrub tickets arrive.
			</p>
		</div>
	);
}
